
import { GetClientAppSettingsResponse, SentrySettings } from "./api/dtos";

export interface ISentrySettings {
    dsn?: string;
    customer: string;
    environment: string;
    sendDefaultPii: boolean;
    enableMetrics: boolean;
    attachStacktrace: boolean;
    debug: boolean;
    application: string;
    release?: string;
    replaysSessionSampleRate: number;
    replaysOnErrorSampleRate: number;
    enableLogs: boolean;
    tracesSampleRate: number;
    enableReportABug: boolean;
}
export interface IAppSettings {
    sentry: ISentrySettings;
};

const ClientAppSettingsResponse: GetClientAppSettingsResponse | undefined = (window as any).APP_SETTINGS;
const getSentryAppSettingValue = <TData>(
    appSettingProperty: keyof SentrySettings | undefined
): TData | undefined => {
    if (!appSettingProperty) {
        return undefined;
    }
    const value: TData | undefined = ClientAppSettingsResponse?.settings?.sentry?.[appSettingProperty] as TData;
    if (value === undefined || value === null) {
        return undefined;
    }
    return value;
};
const getSentryEnvironmentValue = (
    environmentProperty: string | undefined
): string | undefined => {
    if (!environmentProperty) {
        return undefined;
    }
    const value: string | undefined = import.meta.env[environmentProperty];
    if (value === undefined || value === null || value === "") {
        return undefined;
    }
    return value;
};
const getSentryEnvironmentString = (
    environmentProperty: string | undefined
): string | undefined => {
    return getSentryEnvironmentValue(environmentProperty);
};
const getSentryEnvironmentNumber = (
    environmentProperty: string | undefined
): number | undefined => {
    const valueStr = getSentryEnvironmentValue(environmentProperty);
    if (!valueStr) {
        return undefined;
    }
    try {
        return parseFloat(valueStr);
    }
    catch {
        return undefined;
    }
};
const getSentryEnvironmentBoolean = (
    environmentProperty: string | undefined
): boolean | undefined => {
    const valueStr = getSentryEnvironmentValue(environmentProperty);
    if (!valueStr) {
        return undefined;
    }
    const lowerCase = valueStr.toLowerCase();
    if ( lowerCase === "1" || lowerCase === 'true') {
        return true;
    }
    if (lowerCase === "0" || lowerCase === 'false') {
        return false;
    }
    return undefined;
};
const getSentryString = (
    appSettingProperty: keyof SentrySettings | undefined, 
    environmentProperty: string | undefined
) => {
    const appSettingValue = getSentryAppSettingValue<string>(appSettingProperty);
    if (appSettingValue) {
        return appSettingValue;
    }
    const environmentValue = getSentryEnvironmentString(environmentProperty);
    if (environmentValue) {
        return environmentValue;
    }
    return undefined;
};
const getSentryNumber = (
    appSettingProperty: keyof SentrySettings | undefined, 
    environmentProperty: string | undefined
) => {
    const appSettingValue = getSentryAppSettingValue<number>(appSettingProperty);
    if (appSettingValue !== null && appSettingValue !== undefined) {
        return appSettingValue;
    }
    const environmentValue = getSentryEnvironmentNumber(environmentProperty);
    if (environmentValue !== null && environmentValue !== undefined) {
        return environmentValue;
    }
    return undefined;
};
const getSentryBoolean = (
    appSettingProperty: keyof SentrySettings | undefined, 
    environmentProperty: string | undefined
) => {
    const appSettingValue = getSentryAppSettingValue<boolean>(appSettingProperty);
    if (appSettingValue !== null && appSettingValue !== undefined) {
        return appSettingValue;
    }
    const environmentValue = getSentryEnvironmentBoolean(environmentProperty);
    if (environmentValue !== null && environmentValue !== undefined) {
        return environmentValue;
    }
    return undefined;
};


export const AppSettings: IAppSettings = {
    sentry: {
        dsn: getSentryString('dsn', 'VITE_SENTRY_DSN'),
        debug: getSentryBoolean('debug', 'VITE_SENTRY_DEBUG') ?? false,
        enableMetrics: getSentryBoolean('enableMetrics', 'VITE_SENTRY_ENABLE_METRICS') ?? false,
        sendDefaultPii: getSentryBoolean('sendDefaultPii', 'VITE_SENTRY_SEND_DEFAULT_PII') ?? false,
        attachStacktrace: getSentryBoolean('attachStacktrace', 'VITE_SENTRY_ATTACH_STACKTRACE') ?? false,
        customer: getSentryString('customer', 'VITE_SENTRY_CUSTOMER') ?? 'Unknown',
        environment:  getSentryString('environment', 'VITE_SENTRY_ENVIRONMENT') ?? 'Development',
        application: getSentryString(undefined, 'VITE_SENTRY_APPLICATION') ?? 'ResearchMemberApplication',
        release: getSentryString(undefined, 'VITE_SENTRY_RELEASE'),

        enableLogs: getSentryBoolean('enableLogs', 'VITE_SENTRY_ENABLE_LOGS') ?? false,
        replaysOnErrorSampleRate: getSentryNumber('replaysOnErrorSampleRate', 'VITE_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE') ?? 1.0,
        replaysSessionSampleRate: getSentryNumber('replaysSessionSampleRate', 'VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE') ?? 0.1,
        tracesSampleRate: getSentryNumber('tracesSampleRate', 'VITE_SENTRY_TRACE_SAMPLE_RATE') ?? 0.2,

        enableReportABug: getSentryBoolean('enableReportABug', 'VITE_SENTRY_ENABLE_REPORT_A_BUG') ?? false
    }
}